import { Card } from '@/types/Card';
import {
  CARD_KEYS,
  CHANNEL_KEYS,
  CHANNEL_NAMES,
  BANK_KEYS,
  BANK_NAMES,
} from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks'; /**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.HSBC]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/',
    lastUpdated: '2025-06-21',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // HSBC cards
  [CARD_KEYS.HSBC_CASHBACK]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/cashback-signature/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/cashback-signature/',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.HSBC_TRAVEL_INFINITE]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/travel/visa-infinite/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/travel/visa-infinite/',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.HSBC_DIAMOND]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/cashback-titanium/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/cashback-titanium/',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone/',
    lastUpdated: '2025-05-12',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone-signature/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone-signature/',
    lastUpdated: '2025-05-12',
  },
};

export const hsbcCards: { [key: string]: Card } = {
  [CARD_KEYS.HSBC_DIAMOND]: {
    name: '滙豐匯鑽卡',
    summary: '超過20家指定通路3%回饋，最高可達6%',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-diamond.png',
    key: 'hsbc-diamond',
    link: cardLinkMap[CARD_KEYS.HSBC_DIAMOND].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_DIAMOND].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金回饋',
        max_feedback: '每期上限$2,000',
        simplified_condition: '行動支付消費',
        condition:
          '街口支付合作通路享3%回饋，包含百貨購物(廣三SOGO/微風/台北101/誠品)、線上購物(momo/PChome/博客來)、超市量販(家樂福/大潤發/屈臣氏)、便利超商(7-11/全家/萊爾富/OK)、其他通路(中油/麥當勞/星巴克)等',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.JKOPAY],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金回饋',
        max_feedback: '每期上限$2,000',
        simplified_condition: '網購外送平台',
        condition: '指定網購外送平台享3%回饋，涵蓋主要電商與外送服務，滿足日常線上購物需求',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.ONLINE_SHOPPING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金回饋',
        max_feedback: '每期上限$2,000',
        simplified_condition: '線上訂房平台',
        condition: '線上訂房平台如Agoda、Booking.com等享3%回饋，搭配不定期折扣優惠，旅遊訂房更划算',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.BOOKING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金回饋',
        max_feedback: '每期上限$2,000',
        simplified_condition: '影音遊戲平台',
        condition:
          '影音串流平台(Disney+/Netflix)與遊戲平台(Google Play/STEAM)享3%回饋，透過PayPal綁定教學輕鬆賺回饋',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.STREAMING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金回饋',
        max_feedback: '每期上限$2,000',
        simplified_condition: '國外網購',
        condition: '國外網購(限海外交易)享3%回饋，擴大海外購物優惠範圍',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '最高6% 回饋',
        max_feedback: '滿足雙重條件',
        simplified_condition: '回饋達標+帳戶滿額',
        condition:
          '回饋達標(5千點)並符合帳戶滿額條件(30萬)，現金回饋翻倍最高可達6%，可將5千點換為1萬元刷卡金',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限制，超過3%回饋上限後以1%計算',
        channel: '一般消費',
      },
      {
        type: FeatureType.INSTALLMENT,
        feedback: '3.88%起優惠利率',
        max_feedback: '最長30期',
        simplified_condition: '保費分期',
        condition:
          '不限保險公司或險種，保費分期享3.88%起優惠利率，1,000元起最長可分30期，並享1%回饋',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.INSURANCE],
      },
      {
        type: FeatureType.INSTALLMENT,
        feedback: '0利率分期',
        max_feedback: '愛家園專案',
        simplified_condition: '環保節能消費',
        condition: '省油護健康愛家園消費分期0利率，支持環保節能相關消費',
        channel: '環保節能',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '超過20家指定通路3%(上限$2,000)',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_CASHBACK]: {
    name: '滙豐現金回饋卡',
    summary: '海外消費2.5%回饋，指定通路最高5%',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-cashback.jpg',
    key: 'hsbc-cashback',
    link: cardLinkMap[CARD_KEYS.HSBC_CASHBACK].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_CASHBACK].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享2.5%現金回饋，無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 回饋',
        max_feedback: '每月最高回饋1,000元',
        simplified_condition: '指定餐飲、百貨公司',
        condition: '於指定餐飲、百貨公司消費享5%回饋，每月最高回饋1,000元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1.5%現金回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '2.5%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_TRAVEL_INFINITE]: {
    name: '滙豐旅人無限卡',
    summary: '海外消費NT$10=1哩，哩程即時入點',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-travel-infinite.png',
    key: 'hsbc-travel-infinite',
    link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_INFINITE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_INFINITE].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$10=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition:
          '海外消費（含網路交易）每NT$10可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$18=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$18可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: '一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換哩程',
        max_feedback: '15家以上航空公司',
        simplified_condition: '即時入哩',
        condition: '旅遊積分最優1:1起兑換15家以上航空哩程，指定航空哩程兑換即時入哩，隨換隨用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換飯店點數',
        max_feedback: '3大飯店集團',
        simplified_condition: '即時入點',
        condition: '旅遊積分可兑換3大飯店集團點數，兑換即時入點，即刻享受',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '無限次使用',
        simplified_condition: 'Dragon Pass',
        condition:
          '持卡人及一位同行賞客可享有Dragon Pass會員資格，享全球1,000多間機場貴賓室無限次使用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年8次',
        simplified_condition: '單筆消費滿NT$12,000',
        condition: '刷卡單筆消費滿NT$12,000，可享每年8次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
    ],
    domestic_feedback: 'NT$18=1哩',
    international_feedback: 'NT$10=1哩',
    specific_channel_feedback: '機場貴賓室',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE]: {
    name: '滙豐旅人御璽卡',
    summary: '國內外消費NT$15=1哩，哩程即時入點',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-travel-one-signature.jpg',
    key: 'hsbc-travel-one-signature',
    link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$15=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '國內外消費',
        condition: '國內外消費每NT$15可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換哩程',
        max_feedback: '15家以上航空公司',
        simplified_condition: '即時入哩',
        condition: '旅遊積分最優1:1起兑換15家以上航空哩程，指定航空哩程兑換即時入哩，隨換隨用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換飯店點數',
        max_feedback: '3大飯店集團',
        simplified_condition: '即時入點',
        condition: '旅遊積分可兑換3大飯店集團點數，兑換即時入點，即刻享受',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: 'Agoda訂房優惠',
        max_feedback: '最高15%現折',
        simplified_condition: '指定專屬網站',
        condition: '於Agoda指定專屬網站預訂飯店，享全球訂房最高15%現折優惠',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年6次',
        simplified_condition: 'Dragon Pass',
        condition: '持卡人可享有Dragon Pass會員資格，每年可免費使用全球機場貴賓室6次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年4次',
        simplified_condition: '單筆消費滿NT$10,000',
        condition: '刷卡單筆消費滿NT$10,000，可享每年4次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
    ],
    domestic_feedback: 'NT$15=1哩',
    international_feedback: 'NT$15=1哩',
    specific_channel_feedback: '機場貴賓室',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE]: {
    name: '滙豐旅人輕旅卡',
    summary: '國內外消費NT$20=1哩，哩程即時入點',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-travel-one.jpg',
    key: 'hsbc-travel-one',
    link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$20=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '國內外消費',
        condition: '國內外消費每NT$20可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換哩程',
        max_feedback: '15家以上航空公司',
        simplified_condition: '即時入哩',
        condition: '旅遊積分最優1:1起兑換15家以上航空哩程，指定航空哩程兑換即時入哩，隨換隨用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換飯店點數',
        max_feedback: '3大飯店集團',
        simplified_condition: '即時入點',
        condition: '旅遊積分可兑換3大飯店集團點數，兑換即時入點，即刻享受',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: 'Agoda訂房優惠',
        max_feedback: '最高13%現折',
        simplified_condition: '指定專屬網站',
        condition: '於Agoda指定專屬網站預訂飯店，享全球訂房最高13%現折優惠',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年4次',
        simplified_condition: 'Dragon Pass',
        condition: '持卡人可享有Dragon Pass會員資格，每年可免費使用全球機場貴賓室4次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年1次',
        simplified_condition: '單筆消費滿NT$10,000',
        condition: '刷卡單筆消費滿NT$10,000，可享每年1次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
    ],
    domestic_feedback: 'NT$20=1哩',
    international_feedback: 'NT$20=1哩',
    specific_channel_feedback: '機場貴賓室',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
};
